// @oasis-permission-inherits: /jerboa (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { formatDurationMs } from "~/modules/jerboa-client-export/formatters";
import {
  ShadowSimilarityChart,
  ShadowSuccessRateChart,
} from "~/modules/jerboa/ai-edit-fx-shadowing-metrics-chart";
import {
  fetchAiEditFxShadowAnalysisQuery,
  fetchAiEditFxShadowsQuery,
} from "~/modules/jerboa/ai-edit-fx-shadowing-queries";
import { aiEditFxShadowStatusSchema } from "~/modules/jerboa/ai-edit-fx-shadowing-schemas";
import { JerboaCommitLink } from "~/modules/jerboa/components/user-env-card";

function isFailureStatus(status: z.infer<typeof aiEditFxShadowStatusSchema>) {
  return status.endsWith("_FAILED") || status === "VERSION_MISMATCH";
}

function statusBadgeVariant(status: z.infer<typeof aiEditFxShadowStatusSchema>) {
  if (status === "SUCCESS") {
    return "default" as const;
  }
  if (status === "CANCELLED") {
    return "secondary" as const;
  }
  return "outline" as const;
}

export const Route = createFileRoute("/_dash/jerboa/ai-edit-fx-shadowing/")({
  validateSearch: z.object({
    page: z.coerce.number().int().positive().optional().default(1).catch(1),
    status: aiEditFxShadowStatusSchema.optional(),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { page, status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    fetchAiEditFxShadowsQuery.useInfiniteQuery({ params: { status } });
  const {
    data: analysisData,
    error: analysisError,
    isLoading: isLoadingAnalysis,
    refetch: refetchAnalysis,
  } = fetchAiEditFxShadowAnalysisQuery.useQuery({
    params: { days: 7 },
  });

  const totalPages = data?.pages.length ?? 0;
  const targetPageIndex = page ? page - 1 : 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(targetPageIndex);

  useEffect(() => {
    if (targetPageIndex >= totalPages && hasNextPage && !isFetchingNextPage && !error) {
      fetchNextPage();
    }
  }, [targetPageIndex, totalPages, hasNextPage, isFetchingNextPage, error, fetchNextPage]);

  useEffect(() => {
    if (targetPageIndex < totalPages) {
      setCurrentPageIndex(targetPageIndex);
    }
  }, [targetPageIndex, totalPages]);

  const currentPageData = data?.pages[currentPageIndex];
  const items = currentPageData?.results.items ?? [];

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigate({ search: { page: previousPageIndex + 1, status } });
  };

  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigate({ search: { page: nextPageIndex + 1, status } });
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    navigate({ search: { page: pageIndex + 1, status } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">AI Edit FX Shadowing</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>
      {isLoadingAnalysis && <p className="text-sm text-muted-foreground">Loading metrics…</p>}
      {analysisError && (
        <div role="alert" className="flex items-center gap-2 text-sm text-destructive">
          <span>Could not load metrics.</span>
          <Button type="button" variant="outline" size="sm" onClick={() => void refetchAnalysis()}>
            Retry
          </Button>
        </div>
      )}
      {analysisData && analysisData.analysis.buckets.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-md border bg-muted/20 p-4">
            <div className="mb-3">
              <p className="text-sm font-medium">Success Rate over the last 7 days</p>
              <p className="text-xs text-muted-foreground">
                successful / non-cancelled terminal runs per 2-hour bucket
              </p>
            </div>
            <ShadowSuccessRateChart buckets={analysisData.analysis.buckets} />
          </div>
          <div className="rounded-md border bg-muted/20 p-4">
            <div className="mb-3">
              <p className="text-sm font-medium">Similarity over the last 7 days</p>
              <p className="text-xs text-muted-foreground">
                mean similarity across successful runs per 2-hour bucket
              </p>
            </div>
            <ShadowSimilarityChart buckets={analysisData.analysis.buckets} />
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        Status
        <select
          className="h-8 rounded-md border bg-background px-2 text-xs text-foreground"
          value={status ?? ""}
          onChange={(event) =>
            navigate({
              search: {
                page: 1,
                status: event.target.value
                  ? aiEditFxShadowStatusSchema.parse(event.target.value)
                  : undefined,
              },
            })
          }
        >
          <option value="">All statuses</option>
          {aiEditFxShadowStatusSchema.options.map((value) => (
            <option key={value}>{value}</option>
          ))}
        </select>
      </label>
      {error && (
        <div role="alert" className="flex items-center gap-2 text-sm text-destructive">
          <span>Could not load records.</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            Retry
          </Button>
        </div>
      )}
      {isLoading && <p className="text-sm text-muted-foreground">Loading records…</p>}
      {!isLoading && !error && items.length === 0 && (
        <p className="text-sm text-muted-foreground">No matching records found.</p>
      )}
      {!isLoading && currentPageData && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Similarity</TableHead>
              <TableHead>Jerboa Version</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              return (
                <TableRow key={item.id}>
                  <TableCell className="py-1.5">
                    <Link
                      to="/jerboa/ai-edit-fx-shadowing/$resultId"
                      params={{ resultId: item.id }}
                      className="font-mono text-xs text-blue-600 hover:underline"
                    >
                      {item.id}
                    </Link>
                  </TableCell>
                  <TableCell className="py-1.5">
                    {new Date(item.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Badge
                      variant={statusBadgeVariant(item.status)}
                      className={
                        isFailureStatus(item.status)
                          ? "border-destructive/40 text-xs text-destructive"
                          : "text-xs"
                      }
                    >
                      {item.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-1.5 font-mono text-xs">
                    {formatDurationMs(
                      item.durationSeconds == null ? null : item.durationSeconds * 1000
                    )}
                  </TableCell>
                  <TableCell className="py-1.5 font-mono text-xs">
                    {item.comparison?.similarity.toFixed(3) ?? "—"}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs">
                    {item.jerboaVersion ? <JerboaCommitLink sha={item.jerboaVersion} /> : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
